import styles from './EmployeeTable.module.css';
import * as React from 'react';
import { Box, Button, Drawer, IconButton, Portal, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AppStripedDataGrid from 'src/components/AppStripedDataGrid';
import { GridColDef, GridToolbar, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { EmployeesService, GetEmployees_Out, GetMultipleEmployees_Out, TransferType } from 'src/pehra-ts-client';
import { useState } from 'react';
import EmployeeDetailedView from 'src/pages/Employees/components/EmployeeDetailedView';
import { set } from 'react-hook-form';
import TransferToOfficeModal from '../TransferToOfficeModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useNotification from 'src/hooks/useNotification';
import { GridToolbarExport } from '@mui/x-data-grid';

export interface IEmployeeTable {
  rows: GetEmployees_Out[] | undefined;
  officeId: string;
}

const getBgColor = (state: string) => {
  switch (state) {
    case 'AVAILABLE_FOR_TRANSFER':
      return '#ffebb3';
    case 'ACTIVE_IN_PRESENT_OFFICE':
      return '#9be8d6';
    case 'LOCKED':
      return 'red';
    default:
      return 'grey';
  }
};

function MyCustomToolbar(props: any) {
  return (
    <React.Fragment>
      <GridToolbar {...props}>
        <GridToolbarExport
          csvOptions={{
            delimiter: ',',
            utf8WithBom: true
          }}
        />
      </GridToolbar>
    </React.Fragment>
  );
}

const EmployeeTable: React.FC<IEmployeeTable> = ({ rows, officeId }) => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openTransferModal, setOpenTransferModal] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<GetEmployees_Out | null>(null);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [sendNotification] = useNotification();

  const handleOpenTransferModal = () => {
    setOpenTransferModal(true);
  };

  const queryClient = useQueryClient();

  const columnsEmployeeTable: GridColDef[] = [
    {
      field: 'Action',
      headerClassName: 'table--header',
      headerName: '',
      width: 100,
      renderCell: (params: any) => {
        return (
          <Box className={styles.rowDiv}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                setSelectedEmployee(params.row);
                setOpenDrawer(true);
              }}
            >
              View
            </Button>
          </Box>
        );
      }
    },
    {
      field: 'badge_number',
      headerClassName: 'table--header',
      headerName: 'Badge Number',
      width: 100,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.badge_number}</Box>;
      }
    },
    {
      field: 'rank',
      headerClassName: 'table--header',
      headerName: 'Rank',
      width: 100,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.rank || 'N/A'}</Box>;
      }
    },
    {
      field: 'pno_number',
      headerClassName: 'table--header',
      headerName: 'PNO Number',
      width: 100,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.pno_number}</Box>;
      }
    },
    {
      field: 'ehrms_code',
      headerClassName: 'table--header',
      headerName: 'EHRMS Code',
      width: 100,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.ehrms_code}</Box>;
      }
    },
    {
      field: 'name',
      headerClassName: 'table--header',
      headerName: 'Name',
      width: 200,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.name}</Box>;
      }
    },
    {
      field: 'current_office_name',
      headerClassName: 'table--header',
      headerName: 'Current Office',
      width: 100,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.current_office_name}</Box>;
      }
    },
    {
      field: 'current_position',
      headerClassName: 'table--header',
      headerName: 'Current Position',
      width: 200,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.current_position}</Box>;
      }
    },
    {
      field: 'date_of_current_position',
      headerClassName: 'table--header',
      headerName: 'Date of Current Position',
      width: 200,
      renderCell: (params: any) => {
        return <Box className={styles.rowDiv}>{params.row.date_of_current_position}</Box>;
      }
    },
    {
      field: 'state',
      headerClassName: 'table--header',
      headerName: 'State',
      width: 200,
      renderCell: (params: any) => {
        return (
          <Box className={styles.rowDiv}>
            <Box
              sx={{
                backgroundColor: getBgColor(params.row.state),
                padding: '6px',
                borderRadius: '15px'
              }}
            >
              {params.row.state}
            </Box>
          </Box>
        );
      }
    }
  ];

  const handleSelectionChange = (selectionModel: any) => {
    setSelectedRows(selectionModel);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const transferToOffice = async () => {
    try {
      const res = await EmployeesService.transferEmployee({
        employee_ids: selectedRows,
        office_id: officeId,
        transfer_type: TransferType.PERMANENT
      });
      return res;
    } catch (error) {
      throw error;
    }
  };

  const transferToOfficeMutation = useMutation({
    mutationFn: transferToOffice,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employeesList']
      });
      sendNotification({
        msg: 'Employee Transferred Successfully',
        variant: 'success'
      });
      handleCloseModal();
    }
  });

  const handleTransferToOffice = () => {
    transferToOfficeMutation.mutate();
  };

  return (
    <Box className={styles.tableContainer}>
      <Box
        sx={{
          marginTop: '10px',
          marginBottom: '10px',
          background: 'transparent'
        }}
      >
        {selectedRows.length > 0 && (
          <Box className={styles.selectedRowsActionContainer}>
            <Typography variant="h6" className={styles.selectedRowsTitle}>
              {selectedRows.length} Selected
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                handleTransferToOffice();
              }}
            >
              Transfer to this Office
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setOpenModal(true);
              }}
              sx={{
                marginLeft: '10px'
              }}
            >
              Move Temporary to other office
            </Button>
          </Box>
        )}
      </Box>

      <AppStripedDataGrid
        autoHeight
        columns={columnsEmployeeTable}
        rows={rows || []}
        style={{
          width: '100%'
        }}
        getRowId={(row: any) => row.id}
        slots={{
          toolbar: MyCustomToolbar
        }}
        checkboxSelection
        onRowSelectionModelChange={(newSelection: any) => {
          handleSelectionChange(newSelection);
        }}
        disableRowSelectionOnClick
      />
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => {
          setOpenDrawer(false);
          setSelectedEmployee(null);
        }}
        PaperProps={{
          sx: { width: '60%', padding: '20px 0 0 20px' }
        }}
      >
        {selectedEmployee ? (
          <EmployeeDetailedView employeeDetail={selectedEmployee} handleOpenTransferModal={handleOpenTransferModal} />
        ) : null}
      </Drawer>
      <TransferToOfficeModal
        openModal={openModal}
        handleCloseModal={() => {
          setOpenModal(false);
        }}
        employeesList={selectedRows}
      />
    </Box>
  );
};

export default EmployeeTable;
